<template>
  <b-container fluid id="app">
    <TopMenu v-if="currentUser" :currentUser="currentUser" :padminVersion="padminVersion"
      :supportedLanguages="supportedLanguages"/>
    <div class="row content">
      <b-col v-if="showSideMenu" class="col-2" id="side-menu">
        <b-nav vertical>
          <div v-if="initConfig != null && currentUser != null">
            <InitMenu v-if="initConfig.state !== 'initialized'" :initConfig="initConfig" :canSetCloud="currentUser.can_set_cloud" />
            <ScriptsNavMenu />
          </div>
        </b-nav>
      </b-col>
      <b-col id="main" v-bind:class="{ login: isLogin }">
        <router-view id="router-view"/>
        <RestartModal/>
        <b-modal ref="conflict-modal" id="conflict-modal-id" class="padmin-modal" size="sm"
          no-cancel no-ok hide-footer :title="$t('message.conflict')">
          <div v-if="conflictingActivityLog">
            <span v-if="conflictingActivityLog.username == currentUser.username">
              {{$t('message.conflict_same_user_message', {action: conflictingActivityLog.type})}}
            </span>
            <span v-else>
              {{$t('message.conflict_message', {user: conflictingActivityLog.username, action: conflictingActivityLog.type})}}
              <div v-if="conflictingActivityLog.comment"> {{conflictingActivityLog.comment}} </div>
            </span>
          </div>
        </b-modal>
        <b-modal ref="forbidden-modal" id="forbidden-modal-id" class="padmin-modal" size="sm"
          no-cancel no-ok hide-footer :title="$t('message.forbidden')">
          <div>{{$t('message.forbidden_message')}}</div>
        </b-modal>
      </b-col>
    </div>

    <b-modal ref="shallChangePasswordModalRef" id="shall-change-password-modal-id" class="padmin-modal"
      :title="$t('message.shall_change_password')" :hide-footer="true">
      <div>{{$t('message.shall_change_password_message')}}</div>
      <div class="modal-buttons float-right">
        <b-button class="padmin-button primary-button"
          @click="$refs.changeOwnPasswordModalRef.show(); $refs.shallChangePasswordModalRef.hide()">
          {{ $t("message.button_yes") | capitalize }}
        </b-button>
        <b-button class="padmin-button" @click="$refs.shallChangePasswordModalRef.hide()">
          {{ $t("message.button_cancel") | capitalize }}
        </b-button>
      </div>
    </b-modal>

    <b-modal ref="changeOwnPasswordModalRef" :hide-header="true" :hide-footer="true"
      no-close-on-backdrop :no-close-on-esc="mustChangePassword">
      <h3>
        {{ $t("message.change_password") | capitalize }}
      </h3>

      <div v-if="initConfig && initConfig.mode == 'cluster' && currentUser && currentUser.category == 'sys'">
        {{$t("message.change_password_cluster_sys")}}
      </div>

      <div v-if="mustChangePassword && !resetPasswordToken" id="password-expired">
        {{$t("message.password_expired_message")}}
      </div>

      <div>
        <p class="small"><strong>{{ $t("message.pwd_info_title") }}:</strong><br/>
          <ul>
            <li>{{ $t("message.pwd_info_line1") }}</li>
            <li>{{ $t("message.pwd_info_line2") }}</li>
            <li>{{ $t("message.pwd_info_line3") }}</li>
            <li>{{ $t("message.pwd_info_line4") }}</li>
            <li>{{ $t("message.pwd_info_line5") }}</li>
            <li>{{ $t("message.pwd_info_line6") }}</li>
          </ul>
        </p>

        <b-form-group v-if="!resetPasswordToken">
          <label>{{ $t("message.current_password") | capitalize }}</label>
          <b-form-input v-model="currentPassword" type="password" />
        </b-form-group>

        <b-form-group>
          <label>{{ $t("message.new_password") | capitalize }}</label>
          <b-form-input v-model="newPassword" type="password" @change="pwdFieldChanged()"/>
        </b-form-group>

        <b-form-group>
          <label>{{ $t("message.new_password_again") | capitalize }}</label>
          <b-form-input v-model="newPasswordAgain" type="password" @change="pwdFieldChanged()"/>
        </b-form-group>

        <span class="form-error" v-if="passwordChangeError">
          {{ passwordChangeError | title }}
        </span>

        <div class="modal-buttons float-right">
          <b-button class="padmin-button submit-button" @click="changeOwnPasswordSave()"
            :disabled="passwordChangeDisabled">
            <b-spinner v-if="passwordChangeInProgress" small type="grow"/>
            {{ $t("message.button_save") | capitalize }}
          </b-button>
          <b-button v-if="!mustChangePassword" class="padmin-button" @click="changeOwnPasswordCancel()">
            {{ $t("message.button_cancel") | capitalize }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <ToastNotification :message="this.toastMessage" :closed="this.toastClosed" />
  </b-container>

</template>

<script>
import { get, post } from "./http"
import { EventBus } from "./event-bus.js"
import TopMenu from "@/components/TopMenu"
import InitMenu from "@/components/InitMenu"
import ScriptsNavMenu from "@/components/ScriptsNavMenu"
import RestartModal from "@/components/RestartModal"
import ToastNotification from '@/components/ToastNotification.vue'

export default {
  name: "App",

  components: {
    TopMenu,
    ScriptsNavMenu,
    InitMenu,
    RestartModal,
    ToastNotification
  },

  data() {
    return {
      currentUser: null,
      initConfig: null,
      showSideMenu: false,
      isLogin: false,
      padminVersion: null,
      conflictingActivityLog: null,
      mustChangePassword: false,
      currentPassword: '',
      newPassword: '',
      newPasswordAgain: '',
      passwordChangeError: null,
      passwordChangeInProgress: false,
      toastMessage: '',
      resetPasswordToken: null,
      supportedLanguages: [],
      logoutInProgress: false
    }
  },

  created() {
    EventBus.$on('logged in', this.loggedIn)
    EventBus.$on('logged out', this.loggedOut)
    EventBus.$on('conflict', this.conflict)
    EventBus.$on('forbidden', this.forbidden)
    EventBus.$on('change own password', this.changeOwnPasswordOpen)
    EventBus.$on('progress init', this.progressInit)
    EventBus.$on('load supported languages', this.getSupportedLanguages)
    EventBus.$on('padmin version changed', this.getPadminVersion)

    if (this.$route.query.reset_password_token) {
      this.resetPasswordToken = this.$route.query.reset_password_token
      this.mustChangePassword = true
    } else if (!window.localStorage.getItem('auth_token')) {
      this.isLogin = true
      this.$router.replace('/login')
    } else {
      this.progressInit()
    }
  },

  mounted() {
    if (this.resetPasswordToken) {
      this.changeOwnPasswordOpen()
    } else {
      this.getCurrentUser()
    }

    this.getPadminVersion()
    this.getSupportedLanguages()
  },

  computed: {
    passwordChangeDisabled () {
      return this.passwordChangeInProgress || (!this.resetPasswordToken && this.currentPassword.length == 0) ||
        this.newPassword.length == 0 || this.newPasswordAgain.length == 0
    }
  },

  methods: {
    getCurrentUser() {
      get('/current_user').then(
        userResp => {
          if (userResp) {
            this.currentUser = userResp.data
            this.mustChangePassword = this.currentUser.must_change_password
            if (this.mustChangePassword) {
              this.changeOwnPasswordOpen()
            }

            if (this.currentUser.token_expires_in != null) {
              if (this.currentUser.token_expires_in < 0) {
                this.tokenExpired()
                return
              } else {
                setTimeout(() => this.tokenExpired(), this.currentUser.token_expires_in * 1000)
              }
            }

            if (this.currentUser.shall_change_password) {
              this.$refs.shallChangePasswordModalRef.show()
            }
          }
        },
        error => {
          console.log('get current user failed', error)
        }
      )
    },

    getPadminVersion() {
      get('/padmin_version').then(
        versionResp => {
          this.padminVersion = versionResp.data
        },
        error => {
          console.log("get padmin version failed", error)
        }
      )
    },

    getSupportedLanguages() {
      get('/supported_languages').then(
        suppLangResp => {
          this.supportedLanguages = suppLangResp.data['supported_languages'].split(',')
        },
        error => {
          console.log("get supported languages failed", error)
        }
      )
    },

    progressInit() {
      get("/init").then(
        resp => {
          this.initConfig = resp.data
          this.showSideMenu = true
          this.isLogin = false
          if (this.initConfig.mode != 'cloud' || (this.currentUser && this.currentUser.can_set_cloud)) {
            if (this.initConfig.state == 'start') {
              this.$router.replace('/general_script/config/network')
            } else if (this.initConfig.state == 'network set') {
              this.$router.replace('/init/mode')
            }
          }

          if (this.initConfig.state == 'mode set') {
            this.$router.replace('/general_script/config/license')
          } else if (this.initConfig.state == 'license set') {
            this.$router.replace('/init/agung')
          }  else if (this.initConfig.state == 'db initialized') {
            if (this.initConfig.mode == 'cluster') {
              this.$router.replace('/general_script/services/reboot_all_cluster_nodes')
            } else {
              this.$router.replace('/general_script/services/reboot')
            }
          }

        },
        error => {
          console.log("get init conf failed", error)
        }
      )
    },

    loggedIn() {
      this.getCurrentUser()
      this.progressInit()
      this.getPadminVersion()
      this.getSupportedLanguages()
    },

    loggedOut() {
      if (window.localStorage.auth_token && !this.logoutInProgress) {
        this.logoutInProgress = true
        post('/logout').then(resp => {
          this.logoutInProgress = false
          window.localStorage.removeItem('auth_token')
          window.localStorage.removeItem('spqr')
          this.isLogin = true
          this.currentUser = null
          this.showSideMenu = false
          if (this.$route.path != '/login') {
            this.$router.replace('/login')
          }
        })
      }
    },

    tokenExpired() {
      this.toastMessage = this.$t('message.token_expired')

      this.toastTimeout = setTimeout(() => {
        this.toastMessage = null
        this.toastTimeout = null
        this.loggedOut()
      }, 5000)
    },

    conflict(conflictData) {
      this.conflictingActivityLog = conflictData.activity_log
      this.conflictingActivityLog.username = this.conflictingActivityLog.user_upn.split('@')[0]
      this.conflictingActivityLog.type = this.conflictingActivityLog.type.substring(6)
      this.$bvModal.show('conflict-modal-id')
    },

    forbidden() {
      this.$bvModal.show('forbidden-modal-id')
    },

    changeOwnPasswordOpen () {
      this.passwordChangeInProgress = false
      this.passwordChangeError = null
      this.currentPassword = ''
      this.newPassword = ''
      this.newPasswordAgain = ''
      this.$refs.changeOwnPasswordModalRef.show()
    },

    changeOwnPasswordSave () {
      if (this.newPassword !== this.newPasswordAgain) {
        this.passwordChangeError = this.$t('message.passwords_dont_match')
        return
      }

      this.passwordChangeError = ''
      this.passwordChangeInProgress = true

      let data = {'new_password': this.newPassword}
      if (this.resetPasswordToken) {
        data['reset_password_token'] = this.resetPasswordToken
      } else {
        data['current_password'] = this.currentPassword
      }

      post('/current_user/change_password', data).then(resp => {
        this.passwordChangeInProgress = false
        if (resp.data.result) {
          this.$refs.changeOwnPasswordModalRef.hide()
          this.mustChangePassword = false
          if (this.initConfig.mode == 'cluster') {
            if (resp.data.changed_on_this_node_only) {
              this.toastMessage = this.$t('message.password_changed_on_this_node_only')
            } else
            {
              this.toastMessage = this.$t('message.password_changed_on_all_nodes')
            }
          } else {
            this.toastMessage = this.$t('message.password_changed')
          }
          this.toastTimeout = setTimeout(() => {
            this.toastMessage = null
            this.toastTimeout = null
            if (this.resetPasswordToken) {
              this.resetPasswordToken = null
              this.$router.replace('/login')
            }
          }, 10000)
        } else {
          this.passwordChangeError = resp.data.output
        }
      }, err => {
        this.passwordChangeInProgress = false
        console.log('err', err)
      })
    },

    changeOwnPasswordCancel () {
      this.$refs.changeOwnPasswordModalRef.hide()
    },

    toastClosed () {
      if (this.resetPasswordToken) {
        this.resetPasswordToken = null
        this.$router.replace('/login')
      }
      if (this.toastMessage == this.$t('message.token_expired')) {
        this.loggedOut()
      }
      this.toastMessage = null
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout)
      }
    },

    pwdFieldChanged () {
      this.passwordChangeError = ''
    }
  }
}

</script>

<style>
@import "./styles/app.css";
@import "./styles/variables.css";
</style>
